import { useEffect, useRef } from 'react';
import clsx from 'clsx';
import type { Reaction } from '@/lib/api';

interface ReactionPickerProps {
  // The message this picker is for. Used for the toggle call.
  messageId: string;
  // Reactions for the message (so the picker can highlight my-reacted ones).
  reactions: Reaction[];
  // Called when the user picks an emoji. Implementer calls Reactions.toggle(id, emoji).
  onPick: (emoji: string) => void;
  onClose: () => void;
  // Anchor position; picker should render above the message bubble.
  anchorRect?: DOMRect | null;
}

const EMOJIS = ['👍', '❤️', '😂', '🎉', '😮', '😢'] as const;

/**
 * Inline emoji palette. The message action row handles positioning; this
 * component just renders a horizontal pill of emoji buttons. Picking an
 * emoji fires `onPick` then `onClose`. Clicking outside the pill also
 * dismisses the picker so it doesn't stay open over the chat.
 */
export default function ReactionPicker({ reactions, onPick, onClose }: ReactionPickerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDocPointer(e: PointerEvent) {
      const el = containerRef.current;
      if (!el) return;
      if (e.target instanceof Node && !el.contains(e.target)) {
        onClose();
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('pointerdown', onDocPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDocPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={containerRef}
      role="menu"
      aria-label="Reactions"
      className={clsx(
        'inline-flex items-center gap-0.5 rounded-full border border-ink-100 bg-white px-1 py-1 shadow-soft',
        'dark:border-ink-700 dark:bg-ink-800',
      )}
    >
      {EMOJIS.map((emoji) => {
        const reacted = reactions.some((r) => r.emoji === emoji && r.myReacted);
        return (
          <button
            key={emoji}
            type="button"
            role="menuitem"
            aria-label={`React with ${emoji}`}
            aria-pressed={reacted}
            onClick={() => {
              onPick(emoji);
              onClose();
            }}
            className={clsx(
              'flex h-9 w-9 items-center justify-center rounded-full text-lg',
              'transition hover:scale-110 hover:bg-ink-100 dark:hover:bg-ink-700',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
              reacted && 'ring-2 ring-brand-400 dark:ring-brand-500',
            )}
          >
            <span aria-hidden="true">{emoji}</span>
          </button>
        );
      })}
    </div>
  );
}